/**
 * streamsStore — module-scope singleton state machine for Amazon Connect Streams.
 *
 * React components subscribe via `subscribe`/`getSnapshot` (useSyncExternalStore).
 * `streamsBootstrap` dispatches SDK events into this store via the `on*` methods.
 *
 * Call-phase state machine:
 *   idle → ringing → connecting → connected → acw → idle
 *   ringing | connecting → failed → idle (media/setup errors before connect)
 */
import { connectClient, AgentStateType, ContactStateType, createPhoneEndpoint } from './connectClient';
import type {
    AgentStatus,
    AgentStateChange,
    MuteToggleEvent,
    ContactInfo,
    VideoCapabilities,
    ChimeMeetingInfo,
    VoiceEnhancementMode,
    CcpAuthState,
    NetworkConnectionHealth,
    NetworkConnectionStatus,
    MissedCallNotice,
} from '../types/amazon-connect-streams';
import { enrichContactInfoFromStreams, snapshotContactSessionFlags } from './connectContactMeta';
import type { ActiveCallData } from '../app/context/ConnectStreamsContext';
import { ccpLogger, callLogger, setLogContext, clearCallContext } from '../Utils/ccpLogger';
import { serializeStreamsError } from '../Utils/serializeStreamsError';
import * as Sentry from '@sentry/react';
import {
    classifySoftphoneFailure,
    ensureMicAccess,
    extractSoftphoneError,
    queryMicPermission,
    type MicPermissionState,
    type SoftphoneFailureNotice,
} from './softphoneRecovery';
import { captureSoftphoneFailureScreenshot } from './softphoneFailureScreenshot';
import {
    clearNetworkDropState,
    readNetworkDropState,
    writeNetworkDropState,
    type NetworkDropState,
} from './ccpNetworkDropStorage';
import { parseCallHeaderCode } from './incomingCallHandledState';
import { postSetCallEndedByInterpreter } from './setCallEndedByInterpreter';
import {
    attachCcpWebSocketTelemetry,
    clearAgentConnectionState,
    noteCcpAgentRefresh,
    observeAgentConnectionState,
    reportCcpEvent,
    reportContactConnected,
    reportContactEnded,
} from './agentNetworkTelemetry/ccpEvents';

export type { SoftphoneFailureNotice, MicPermissionState, CcpAuthState, NetworkConnectionHealth, MissedCallNotice };

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type CallPhase = 'idle' | 'ringing' | 'connecting' | 'connected' | 'acw' | 'failed';

const PRE_CONNECT_CALL_PHASES: CallPhase[] = ['ringing', 'connecting'];

/** Contact-attribute keys a contact flow may use to declare the call type. */
const CALL_TYPE_ATTRIBUTE_KEYS = ['type', 'callType', 'CallType', 'call_type'] as const;

/**
 * How long a modal-accept stays armed. Must comfortably exceed real BE→Connect→CCP
 * delivery latency, observed up to ~21s in production under parallel load.
 */
const AUTO_ACCEPT_ARM_TTL_MS = 45_000;

export interface ConferenceParticipant {
    connectionId: string;
    phoneNumber?: string;
    type: string;
    isOnHold: boolean;
    isConnected: boolean;
    state: string;
    stateDurationMs: number;
    isMuted: boolean;
    /** From voiceConnection.canResumeParticipant() (Streams ≥ 2.26). null = unknown. */
    canResume: boolean | null;
}

export interface AudioDeviceOption {
    deviceId: string;
    label: string;
    kind: 'audioinput' | 'audiooutput';
}

export interface Snapshot {
    isInitialized: boolean;
    isLoggedIn: boolean;
    /** Streams core auth lifecycle — distinct from agent login. */
    ccpAuthState: CcpAuthState;
    agentName: string;
    agentEmail: string;
    agentStatus: AgentStatus;
    isAgentMuted: boolean;
    dialableCountries: string[];
    voiceEnhancementMode: VoiceEnhancementMode;
    microphoneDeviceId: string | null;
    speakerDeviceId: string | null;
    microphones: AudioDeviceOption[];
    speakers: AudioDeviceOption[];

    callPhase: CallPhase;
    contactId: string | null;
    contact: ContactInfo | null;
    callDuration: number;
    /** True only when every actionable participant is on hold (Hold All active). */
    isCallOnHold: boolean;
    videoCapabilities: VideoCapabilities | null;
    videoConnectionInfo: ChimeMeetingInfo | null;
    conferenceParticipants: ConferenceParticipant[];
    isAddingParticipant: boolean;
    /** From contact.canTransferContact() when present (added 2.26, removed 2.27.3+). null = unknown / unavailable. */
    canTransferContact: boolean | null;

    /** Last softphone/media failure shown until dismissed or recovered. */
    softphoneFailure: SoftphoneFailureNotice | null;
    micPermission: MicPermissionState;

    activeCallData: ActiveCallData | null;
    autoAcceptArmed: boolean;
    /** True while an outbound softphone probe dial is in flight. */
    softphoneProbeInFlight: boolean;

    networkConnection: NetworkConnectionHealth;
    /** Browser reachability — reconnect/disarm signal (navigator.onLine). */
    browserOnline: boolean;
    /** True after network-drop auto-end; widget shows “Call Dropped.” notice. */
    networkDropNotice: boolean;
    lastMissedCall: MissedCallNotice | null;
    hasTwoActiveParticipants: boolean;
    isUnderSupervision: boolean;
}

export type StoreEvent =
    | { type: 'agent:initialized' }
    | { type: 'contact:connecting'; contactId: string }
    | { type: 'contact:connected'; contactId: string }
    | { type: 'contact:missed'; contactId: string; diagnostics: Record<string, any> }
    | { type: 'contact:ended'; contactId: string }
    | { type: 'contact:destroyed'; contactId: string }
    | { type: 'softphone:failure'; failure: SoftphoneFailureNotice }
    | { type: 'softphoneProbe:connected'; contactId: string }
    | { type: 'contact:detached'; contactId: string | null; hadSoftphoneFailure: boolean }
    | { type: 'autoAccept:expired' }
    | { type: 'auth:authorize_success' }
    | { type: 'logout'; reinitCcp?: boolean };

type Listener = () => void;
type EventListener = (event: StoreEvent) => void;

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

const initialSnapshot: Snapshot = {
    isInitialized: false,
    isLoggedIn: false,
    ccpAuthState: 'ccp_loading',
    agentName: '',
    agentEmail: '',
    agentStatus: 'Offline',
    isAgentMuted: false,
    dialableCountries: [],
    voiceEnhancementMode: 'NONE',
    microphoneDeviceId: null,
    speakerDeviceId: null,
    microphones: [],
    speakers: [],

    callPhase: 'idle',
    contactId: null,
    contact: null,
    callDuration: 0,
    isCallOnHold: false,
    videoCapabilities: null,
    videoConnectionInfo: null,
    conferenceParticipants: [],
    isAddingParticipant: false,
    canTransferContact: null,
    softphoneFailure: null,
    micPermission: 'unknown',

    activeCallData: null,
    autoAcceptArmed: false,
    softphoneProbeInFlight: false,

    networkConnection: {
        status: 'unknown',
        updatedAt: 0,
        websocketConnected: true,
    },
    browserOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
    networkDropNotice: false,
    lastMissedCall: null,
    hasTwoActiveParticipants: false,
    isUnderSupervision: false,
};

// ---------------------------------------------------------------------------
// Module-scope mutable internals (NOT part of snapshot)
// ---------------------------------------------------------------------------

let snapshot: Snapshot = initialSnapshot;
const listeners = new Set<Listener>();
const eventListeners = new Set<EventListener>();

let agentRef: connect.Agent | null = null;
let autoAcceptTimer: ReturnType<typeof setTimeout> | null = null;
let autoAcceptCallHeaderId: string | null = null;
/** Incoming call metadata keyed by Connect contactId (from Firebase / SignalR before CCP contact lands). */
let incomingCallMetaByContactId = new Map<string, ActiveCallData>();
let callDurationTimer: ReturnType<typeof setInterval> | null = null;
/** Polls agent.getNetworkConnectionStatus while disconnected/failed/websocket-lost. */
let networkConnectionPollTimer: ReturnType<typeof setInterval> | null = null;
/** Single timer for network-drop arm (25s) or post-reload clear (30s). */
let networkDropTimer: ReturnType<typeof setTimeout> | null = null;
/** One-shot so pagehide (and optional beforeunload) cannot double-write timedOut. */
let networkDropUnloadHandled = false;
/** Prevent re-entrant auto-end. */
let networkDropEnding = false;

const NETWORK_DROP_ARM_MS = 25_000;
const NETWORK_DROP_RELOAD_CLEAR_MS = 30_000;
let shouldAutoConference = false;
let disconnectedParticipantIds = new Set<string>();
let isAcceptingCall = false;
let isDialing = false;
let isAddingParticipantInFlight = false;
let addParticipantTimer: ReturnType<typeof setTimeout> | null = null;
/** Streams ≥ 2.26 canResumeParticipant cache, keyed by connectionId */
let canResumeByConnectionId = new Map<string, boolean>();
/** Avoid double-subscribing VoiceConnection hold/resume events */
let holdEventSubscribedConnectionIds = new Set<string>();
/** Optimistic hold flags so UI updates immediately while Streams catches up */
let optimisticHoldByConnectionId = new Map<string, boolean>();
let holdAllInFlight = false;
let participantRefreshTimer: ReturnType<typeof setTimeout> | null = null;
let micPermissionStatus: PermissionStatus | null = null;
let micPermissionChangeHandler: (() => void) | null = null;

const ADD_PARTICIPANT_TIMEOUT_MS = 45_000;
const TERMINAL_CONNECTION_STATES = new Set(['disconnected', 'ended']);
const VOICE_ENHANCEMENT_MODES: VoiceEnhancementMode[] = [
    'NONE',
    'NOISE_SUPPRESSION',
    'VOICE_ISOLATION',
];

function normalizeVoiceEnhancementMode(mode: unknown): VoiceEnhancementMode {
    if (typeof mode === 'string' && (VOICE_ENHANCEMENT_MODES as string[]).includes(mode)) {
        return mode as VoiceEnhancementMode;
    }
    return 'NONE';
}

function toAudioDeviceOption(device: MediaDeviceInfo): AudioDeviceOption | null {
    if (device.kind !== 'audioinput' && device.kind !== 'audiooutput') return null;
    if (!device.deviceId) return null;
    const fallback =
        device.kind === 'audioinput' ? 'Microphone' : 'Speaker';
    return {
        deviceId: device.deviceId,
        label: device.label?.trim() || `${fallback} (${device.deviceId.slice(0, 8)})`,
        kind: device.kind,
    };
}

async function listMediaDevices(options?: { requestPermission?: boolean }): Promise<MediaDeviceInfo[]> {
    try {
        const getFrame = (connectClient.core as any)?.getFrameMediaDevices;
        if (typeof getFrame === 'function') {
            const framed = await getFrame.call(connectClient.core, 3000);
            if (Array.isArray(framed) && framed.length > 0) return framed;
        }
    } catch (e) {
        ccpLogger.warn('getFrameMediaDevices failed — falling back to enumerateDevices', {
            error: String(e),
        });
    }
    if (!navigator.mediaDevices?.enumerateDevices) return [];
    if (options?.requestPermission !== false) {
        try {
            // Prompt once so device labels are populated (skip during active calls).
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            stream.getTracks().forEach((t) => t.stop());
        } catch {
            /* permission may already exist or be denied — still try enumerate */
        }
    }
    return navigator.mediaDevices.enumerateDevices();
}

function clearVoiceConnectionSubscriptions() {
    canResumeByConnectionId = new Map();
    holdEventSubscribedConnectionIds = new Set();
    optimisticHoldByConnectionId = new Map();
    holdAllInFlight = false;
    if (participantRefreshTimer) {
        clearTimeout(participantRefreshTimer);
        participantRefreshTimer = null;
    }
}

function reportSoftphoneFailure(source: string, err?: unknown): void {
    const failure = classifySoftphoneFailure(err, source);
    const contactId = snapshot.contactId || snapshot.contact?.contactId || null;
    callLogger.error('Softphone failure reported', {
        source,
        kind: failure.kind,
        errorType: failure.errorType,
        callPhase: snapshot.callPhase,
        agentStatus: snapshot.agentStatus,
        contactId,
    });

    // Every softphone error: full-page screenshot → Sentry attachment (fire-and-forget).
    void captureSoftphoneFailureScreenshot({
        failure,
        contactId,
        callPhase: snapshot.callPhase,
    });

    // Login/setup signalling noise — log only; real call failures surface once a contact is active.
    if (snapshot.callPhase === 'idle') return;

    // Connect often fires onSoftphoneError, contact.onError, and accept_failure for one incident.
    // Surface the first failure only until the user clears it or the contact recovers.
    if (snapshot.softphoneFailure) {
        callLogger.breadcrumb('Softphone failure duplicate suppressed', {
            source,
            errorType: failure.errorType,
            contactId,
        });
        return;
    }

    const patch: Partial<Snapshot> = { softphoneFailure: failure };
    if (PRE_CONNECT_CALL_PHASES.includes(snapshot.callPhase)) {
        patch.callPhase = 'failed';
        isAcceptingCall = false;
        // Auto-accept stays armed: a softphone failure during setup is exactly when Connect
        // re-offers the call, and that re-ring must auto-accept without a second click.
    }
    if (failure.kind === 'microphone') {
        patch.micPermission = 'denied';
    }
    update(patch);
    emit({ type: 'softphone:failure', failure });
}

function stopMicPermissionMonitoring() {
    if (micPermissionStatus && micPermissionChangeHandler) {
        micPermissionStatus.removeEventListener('change', micPermissionChangeHandler);
    }
    micPermissionStatus = null;
    micPermissionChangeHandler = null;
}

function scheduleParticipantRefresh(delayMs = 150) {
    if (participantRefreshTimer) clearTimeout(participantRefreshTimer);
    participantRefreshTimer = setTimeout(() => {
        participantRefreshTimer = null;
        streamsStore.refreshParticipants();
    }, delayMs);
}

function isConnectionOnHold(conn: any): boolean {
    const id = safe(() => conn.getConnectionId(), null);
    if (id && optimisticHoldByConnectionId.has(id)) {
        return optimisticHoldByConnectionId.get(id)!;
    }
    const st = safe(() => conn.getState?.()?.type, null);
    return st === 'hold' || safe(() => conn.isOnHold?.(), false);
}

/** Non-agent connections that are live (connected) or already on hold. */
function getActionableParticipantConnections(c: connect.Contact): any[] {
    const agentConnId = safe(() => c.getAgentConnection()?.getConnectionId(), null);
    return safe(() => c.getConnections(), []).filter((conn: any) => {
        if (!isHoldableConnection(conn, agentConnId)) return false;
        return safe(() => conn.isConnected(), false) || isConnectionOnHold(conn);
    });
}

/** True only when every actionable participant is on hold (drives Hold All active state). */
function areAllParticipantsOnHold(c: connect.Contact): boolean {
    const actionable = getActionableParticipantConnections(c);
    if (actionable.length === 0) return false;
    return actionable.every((conn) => isConnectionOnHold(conn));
}

function syncAllHoldFlag(c: connect.Contact | null) {
    if (holdAllInFlight) return;
    const allHeld = c ? areAllParticipantsOnHold(c) : false;
    if (snapshot.isCallOnHold !== allHeld) {
        update({ isCallOnHold: allHeld });
    }
}

function setOptimisticHold(connectionId: string | undefined, onHold: boolean) {
    if (!connectionId) return;
    optimisticHoldByConnectionId.set(connectionId, onHold);
}

function clearOptimisticHoldIfMatches(conn: any, connectionId: string) {
    if (!optimisticHoldByConnectionId.has(connectionId)) return;
    const live =
        safe(() => conn.getState?.()?.type, null) === 'hold' ||
        safe(() => conn.isOnHold?.(), false);
    if (live === optimisticHoldByConnectionId.get(connectionId)) {
        optimisticHoldByConnectionId.delete(connectionId);
    }
}

function holdConnection(conn: any): Promise<void> {
    const connectionId = safe(() => conn.getConnectionId(), undefined);
    setOptimisticHold(connectionId, true);
    return new Promise((resolve) => {
        conn.hold({
            success: () => {
                callLogger.breadcrumb('Connection held', { connectionId });
                resolve();
            },
            failure: (err: any) => {
                callLogger.error('Failed to hold', { error: String(err) });
                if (connectionId) optimisticHoldByConnectionId.delete(connectionId);
                handleStaleConnectionOnHoldError(err, connectionId);
                resolve();
            },
        });
    });
}

function resumeConnection(conn: any): Promise<void> {
    const connectionId = safe(() => conn.getConnectionId(), undefined);
    setOptimisticHold(connectionId, false);
    return new Promise((resolve) => {
        conn.resume({
            success: () => {
                callLogger.breadcrumb('Connection resumed', { connectionId });
                resolve();
            },
            failure: (err: any) => {
                callLogger.error('Failed to resume', { error: String(err) });
                if (connectionId) optimisticHoldByConnectionId.delete(connectionId);
                handleStaleConnectionOnHoldError(err, connectionId);
                resolve();
            },
        });
    });
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

const AWS_CONNECT_CCP_URL = process.env.REACT_APP_AWS_CONNECT_URL || '';

function setIsAddingParticipant(value: boolean, reason: string) {
    if (snapshot.isAddingParticipant === value) return;
    callLogger.breadcrumb('isAddingParticipant transition', {
        from: snapshot.isAddingParticipant,
        to: value,
        reason,
    });
    update({ isAddingParticipant: value });
}

function clearAddParticipantTimer() {
    if (addParticipantTimer) {
        clearTimeout(addParticipantTimer);
        addParticipantTimer = null;
    }
}

function finishAddParticipant(reason: string) {
    clearAddParticipantTimer();
    isAddingParticipantInFlight = false;
    setIsAddingParticipant(false, reason);
}

function isConnectionNotFoundError(err: unknown): boolean {
    const serialized = serializeStreamsError(err);
    const type = String(serialized.type ?? '');
    const message = String(serialized.message ?? serialized.value ?? err ?? '');
    return (
        type.includes('ConnectionNotFoundException') ||
        message.includes('Connection not found')
    );
}

function handleStaleConnectionOnHoldError(err: unknown, connectionId?: string) {
    if (!isConnectionNotFoundError(err)) return;
    if (connectionId) {
        disconnectedParticipantIds.add(connectionId);
        callLogger.warn('Stale connection dropped after hold failure', { connectionId });
    }
    streamsStore.refreshParticipants();
}

function isHoldableConnection(conn: any, agentConnId: string | null): boolean {
    const connId = safe(() => conn.getConnectionId(), null);
    if (!connId || connId === agentConnId) return false;
    if (disconnectedParticipantIds.has(connId)) return false;
    const st = safe(() => conn.getState?.()?.type, null);
    if (st && TERMINAL_CONNECTION_STATES.has(st)) return false;
    return true;
}

function isHoldableForHoldAction(conn: any, agentConnId: string | null): boolean {
    if (!isHoldableConnection(conn, agentConnId)) return false;
    if (isConnectionOnHold(conn)) return false;
    return safe(() => conn.isConnected(), false);
}

function isHoldableForResumeAction(conn: any, agentConnId: string | null): boolean {
    if (!isHoldableConnection(conn, agentConnId)) return false;
    return isConnectionOnHold(conn);
}

function getHoldableConnections(c: connect.Contact, mode: 'hold' | 'resume'): any[] {
    const agentConn = c.getAgentConnection();
    const agentConnId = agentConn ? agentConn.getConnectionId() : null;
    const connections = c.getConnections();
    const predicate = mode === 'hold' ? isHoldableForHoldAction : isHoldableForResumeAction;
    return connections.filter((conn: any) => predicate(conn, agentConnId));
}

function clearAddingParticipantIfOutboundSettled(c: connect.Contact) {
    if (!snapshot.isAddingParticipant) return;
    const initialConn = c.getInitialConnection();
    const outboundSettled = c.getConnections().some((cc: any) => {
        if (cc === initialConn) return false;
        const st = safe(() => cc.getState?.()?.type, null);
        return safe(() => cc.isConnected(), false) || (st != null && TERMINAL_CONNECTION_STATES.has(st));
    });
    if (outboundSettled) {
        finishAddParticipant('outbound connection settled (refreshParticipants safety net)');
    }
}

function notify() {
    listeners.forEach((l) => l());
}

function emit(event: StoreEvent) {
    eventListeners.forEach((l) => l(event));
}

function update(patch: Partial<Snapshot>) {
    snapshot = { ...snapshot, ...patch };
    notify();
}

function safe<T>(fn: () => T, fallback: T): T {
    try {
        return fn();
    } catch {
        return fallback;
    }
}

function getContactById(contactId: string | null): connect.Contact | null {
    if (!contactId || !agentRef) return null;
    try {
        const contacts = agentRef.getContacts();
        return contacts.find((c: any) => c.getContactId() === contactId) ?? null;
    } catch {
        return null;
    }
}

function buildContactInfo(c: connect.Contact): ContactInfo {
    const info: ContactInfo = {
        contactId: c.getContactId(),
        type: c.getType(),
        state: c.getState().type,
    };

    const initialConnection = c.getInitialConnection();
    if (initialConnection) {
        const endpoint = initialConnection.getEndpoint();
        if (endpoint?.phoneNumber) info.phoneNumber = endpoint.phoneNumber;
        if (endpoint?.name && typeof endpoint.name === 'string' && endpoint.name.trim()) {
            info.callerDisplayName = endpoint.name.trim();
        }
    }

    try {
        const raw = c.getAttributes();
        const attributes: Record<string, string> = {};
        if (raw && typeof raw === 'object') {
            Object.entries(raw).forEach(([key, attr]) => {
                const name =
                    attr && typeof attr === 'object' && 'name' in attr
                        ? (attr as { name: string }).name
                        : key;
                const value =
                    attr && typeof attr === 'object' && 'value' in attr
                        ? (attr as { value: string }).value
                        : typeof attr === 'string'
                            ? attr
                            : '';
                if (name) attributes[name] = value;
            });
        }
        info.attributes = attributes;

        if (!info.callerDisplayName && Object.keys(attributes).length > 0) {
            const nameKeys = [
                'CustomerName', 'RequesterName', 'customer_name', 'Requester',
                'CallerName', 'Name', 'requester', 'caller_name', 'Customer',
                'requesterFullName', 'customerName',
            ];
            for (const k of nameKeys) {
                const v = attributes[k];
                if (v && typeof v === 'string' && v.trim()) {
                    info.callerDisplayName = v.trim();
                    break;
                }
            }
            if (!info.callerDisplayName) {
                const first = attributes.requesterFirstName || attributes.RequesterFirstName || '';
                const last = attributes.requesterLastName || attributes.RequesterLastName || '';
                const combined = `${first} ${last}`.trim();
                if (combined) info.callerDisplayName = combined;
            }
        }
    } catch (e) {
        callLogger.warn('Could not read contact attributes', { error: String(e) });
    }

    return enrichContactInfoFromStreams(c, info);
}

function snapshotVideoCapabilities(c: connect.Contact): VideoCapabilities {
    return {
        hasVideoRTC: safe(() => typeof (c as any).hasVideoRTCCapabilities === 'function' && (c as any).hasVideoRTCCapabilities(), false),
        canSendVideo: safe(() => typeof (c as any).canAgentSendVideo === 'function' && (c as any).canAgentSendVideo(), false),
        canReceiveVideo: safe(() => typeof (c as any).canAgentReceiveVideo === 'function' && (c as any).canAgentReceiveVideo(), false),
    };
}

function snapshotParticipants(c: connect.Contact): ConferenceParticipant[] {
    try {
        return c
            .getConnections()
            .map((conn: any) => {
                const endpoint = conn.getEndpoint();
                const st = conn.getState();
                const connectionId = conn.getConnectionId();
                clearOptimisticHoldIfMatches(conn, connectionId);
                const canResumeCached = canResumeByConnectionId.has(connectionId)
                    ? canResumeByConnectionId.get(connectionId)!
                    : null;
                const onHold = isConnectionOnHold(conn);
                return {
                    connectionId,
                    phoneNumber: endpoint?.phoneNumber || undefined,
                    type: conn.getType(),
                    isOnHold: onHold,
                    isConnected: onHold ? true : conn.isConnected(),
                    state: onHold ? 'hold' : st?.type || 'unknown',
                    stateDurationMs: safe(() => conn.getStateDuration() || 0, 0),
                    isMuted: safe(() => (typeof conn.isMute === 'function' ? conn.isMute() : false), false),
                    canResume: canResumeCached,
                };
            })
            .filter((p: ConferenceParticipant) => !disconnectedParticipantIds.has(p.connectionId));
    } catch (e) {
        callLogger.error('snapshotParticipants failed', { error: String(e) });
        return [];
    }
}

/**
 * Subscribe VoiceConnection hold/resume + canResume events (Streams ≥ 2.26).
 * Debounced refresh only — do not drive global Hold All from these events
 * (connection state lags after resume and caused UI flicker).
 */
function ensureVoiceConnectionHoldSubscriptions(c: connect.Contact) {
    const agentConnId = safe(() => c.getAgentConnection()?.getConnectionId(), null);
    const connections = safe(() => c.getConnections(), []);

    for (const conn of connections) {
        const connectionId = safe(() => conn.getConnectionId(), null);
        if (!connectionId || connectionId === agentConnId) continue;
        if (holdEventSubscribedConnectionIds.has(connectionId)) continue;
        if (typeof (conn as any).onParticipantHold !== 'function') continue;

        holdEventSubscribedConnectionIds.add(connectionId);

        try {
            (conn as any).onParticipantHold(() => {
                callLogger.breadcrumb('onParticipantHold', { connectionId });
                scheduleParticipantRefresh();
            });
            (conn as any).onParticipantResume(() => {
                callLogger.breadcrumb('onParticipantResume', { connectionId });
                scheduleParticipantRefresh();
            });
            if (typeof (conn as any).onCanResumeParticipantUpdated === 'function') {
                (conn as any).onCanResumeParticipantUpdated(() => {
                    void refreshCanResumeForConnection(conn, connectionId);
                });
            }
            void refreshCanResumeForConnection(conn, connectionId);
        } catch (e) {
            callLogger.warn('Failed to subscribe voice hold events', {
                connectionId,
                error: String(e),
            });
            holdEventSubscribedConnectionIds.delete(connectionId);
        }
    }
}

async function refreshCanResumeForConnection(conn: any, connectionId: string) {
    if (typeof conn.canResumeParticipant !== 'function') return;
    try {
        const canResume = await conn.canResumeParticipant();
        const prev = canResumeByConnectionId.get(connectionId);
        if (prev === canResume) return;
        canResumeByConnectionId.set(connectionId, !!canResume);
        callLogger.breadcrumb('canResumeParticipant updated', { connectionId, canResume: !!canResume });
        streamsStore.refreshParticipants();
    } catch (e) {
        callLogger.warn('canResumeParticipant failed', { connectionId, error: String(e) });
    }
}

/** Hydrate contact ARN + transfer eligibility when Streams exposes those APIs (removed in 2.27.3+). */
async function hydrateContactCapabilities(contactId: string) {
    const c = getContactById(contactId);
    if (!c) return;

    let contactArn: string | undefined;
    if (typeof (c as any).getContactArn === 'function') {
        try {
            contactArn = await (c as any).getContactArn();
        } catch (e) {
            callLogger.warn('getContactArn failed', { contactId, error: String(e) });
        }
    }

    let canTransfer: boolean | null = snapshot.canTransferContact;
    if (typeof (c as any).canTransferContact === 'function') {
        try {
            canTransfer = !!(await (c as any).canTransferContact());
        } catch (e) {
            callLogger.warn('canTransferContact failed', { contactId, error: String(e) });
            canTransfer = null;
        }
    }

    const patch: Partial<Snapshot> = {};
    if (canTransfer !== snapshot.canTransferContact) {
        patch.canTransferContact = canTransfer;
    }
    if (contactArn && snapshot.contact?.contactId === contactId) {
        if (snapshot.contact.contactArn !== contactArn) {
            patch.contact = { ...snapshot.contact, contactArn };
        }
    }
    if (Object.keys(patch).length > 0) update(patch);
}

function hydrateContactSessionState(contactId: string) {
    const c = getContactById(contactId);
    if (!c) return;

    const flags = snapshotContactSessionFlags(c);
    const info = buildContactInfo(c);
    const existing = snapshot.contact?.contactId === contactId ? snapshot.contact : null;

    const patch: Partial<Snapshot> = {
        hasTwoActiveParticipants: flags.hasTwoActiveParticipants,
        isUnderSupervision: flags.isUnderSupervision,
    };

    if (existing) {
        patch.contact = {
            ...existing,
            ...info,
            contactArn: existing.contactArn ?? info.contactArn,
        };
    }

    update(patch);
}

const NETWORK_CONNECTION_POLL_MS = 1000;

type CcpNetworkSentryEvent =
    | 'NetworkConnectionStatus'
    | 'onWebSocketConnectionLost'
    | 'onWebSocketConnectionGained';

/**
 * Send CCP network / websocket events to Sentry Logs + Issues.
 * Filter Issues/Logs with tag ccp.event:NetworkConnectionStatus (etc.).
 */
function reportCcpNetworkEventToSentry(
    eventName: CcpNetworkSentryEvent,
    level: 'info' | 'warning' | 'error',
    data: Record<string, unknown> = {},
) {
    const statusSuffix =
        typeof data.status === 'string' && data.status ? ` status=${data.status}` : '';
    const body = `${eventName}${statusSuffix}`;
    const prefixed = `[CCP] ${body}`;

    Sentry.withScope((scope) => {
        scope.setTag('ccp.event', eventName);
        scope.setTag('module', 'ccp');
        if (typeof data.status === 'string') {
            scope.setTag('ccp.network_status', data.status);
        }
        if (typeof data.websocketConnected === 'boolean') {
            scope.setTag('ccp.websocket_connected', String(data.websocketConnected));
        }
        if (typeof data.previousStatus === 'string') {
            scope.setTag('ccp.previous_network_status', data.previousStatus);
        }
        scope.setContext('ccpNetwork', {
            eventName,
            callPhase: snapshot.callPhase,
            agentStatus: snapshot.agentStatus,
            contactId: snapshot.contact?.contactId ?? null,
            ...data,
        });

        // error → ccpLogger.error already captureMessages with [CCP] prefix (inherits tags).
        if (level === 'error') {
            ccpLogger.error(body, data);
            return;
        }

        if (level === 'warning') ccpLogger.warn(prefixed, data);
        else ccpLogger.info(prefixed, data);

        Sentry.captureMessage(prefixed, level);
    });
}

function shouldPollNetworkConnection(health: NetworkConnectionHealth): boolean {
    return health.status === 'disconnected' || health.status === 'failed';
}

function stopNetworkConnectionPolling() {
    if (!networkConnectionPollTimer) return;
    clearInterval(networkConnectionPollTimer);
    networkConnectionPollTimer = null;
}

function startNetworkConnectionPolling() {
    if (networkConnectionPollTimer) return;
    ccpLogger.info('Starting network connection status poll', {
        intervalMs: NETWORK_CONNECTION_POLL_MS,
        status: snapshot.networkConnection.status,
        websocketConnected: snapshot.networkConnection.websocketConnected,
    });
    networkConnectionPollTimer = setInterval(() => {
        const agent = agentRef;
        if (!agent) return;
        void syncNetworkConnectionFromAgent(agent);
    }, NETWORK_CONNECTION_POLL_MS);
}

function reconcileNetworkConnectionPolling() {
    if (shouldPollNetworkConnection(snapshot.networkConnection)) {
        startNetworkConnectionPolling();
    } else {
        stopNetworkConnectionPolling();
    }
}

// ---------------------------------------------------------------------------
// CCP network-drop auto-end (one storage key, one timer, one reconcile)
// Disconnect: Streams NetworkConnectionStatus (disconnected|failed).
// Reconnect / disarm / post-timeout end: navigator.onLine only.
// ---------------------------------------------------------------------------

/** SDK disconnect signal — same as Reconnecting UI “down” half. */
function isSdkNetworkDown(health: NetworkConnectionHealth = snapshot.networkConnection): boolean {
    return health.status === 'disconnected' || health.status === 'failed';
}

/** Browser back online — reconnect path (not Streams `connected`). */
function isBrowserOnline(): boolean {
    return snapshot.browserOnline;
}

function clearNetworkDropTimer() {
    if (!networkDropTimer) return;
    clearTimeout(networkDropTimer);
    networkDropTimer = null;
}

function resetNetworkDropDomain() {
    clearNetworkDropTimer();
    clearNetworkDropState();
}

function currentDropCallMeta(): { callHeaderId: string | null; contactId: string | null } {
    const callHeaderId =
        snapshot.activeCallData?.callHeaderId != null
            ? String(snapshot.activeCallData.callHeaderId)
            : null;
    return {
        callHeaderId,
        contactId: snapshot.contactId,
    };
}

function findLiveContact(): connect.Contact | null {
    if (!agentRef) return null;
    try {
        const contacts = agentRef.getContacts() || [];
        for (const c of contacts) {
            const type = safe(() => c.getState()?.type, null);
            if (
                type === ContactStateType.CONNECTED ||
                type === ContactStateType.CONNECTING
            ) {
                return c;
            }
        }
    } catch {
        /* ignore */
    }
    return null;
}

function hasLiveContact(): boolean {
    return findLiveContact() != null;
}

function scheduleNetworkDropTimer(delayMs: number, onFire: () => void) {
    clearNetworkDropTimer();
    networkDropTimer = setTimeout(() => {
        networkDropTimer = null;
        onFire();
    }, delayMs);
}

function armUnhealthyDropTimer(startedAt: number) {
    const remaining = Math.max(0, NETWORK_DROP_ARM_MS - (Date.now() - startedAt));
    scheduleNetworkDropTimer(remaining, () => {
        const state = readNetworkDropState();
        if (state.phase !== 'armed') return;
        // Reconnect path: browser online before 25s → disarm, do not timedOut.
        if (isBrowserOnline()) {
            reconcileNetworkDrop('timer-25s-browser-online');
            return;
        }
        if (snapshot.callPhase !== 'connected' || !isSdkNetworkDown()) {
            reconcileNetworkDrop('timer-25s-stale');
            return;
        }
        writeNetworkDropState({ ...state, phase: 'timedOut' });
        callLogger.warn('CCP network drop timed out (25s offline)', {
            contactId: state.contactId,
            callHeaderId: state.callHeaderId,
        });
        reconcileNetworkDrop('timer-25s');
    });
}

function armReloadClearTimer() {
    scheduleNetworkDropTimer(NETWORK_DROP_RELOAD_CLEAR_MS, () => {
        const state = readNetworkDropState();
        if (state.phase === 'timedOut' && state.fromReload) {
            callLogger.info('CCP network drop: no live contact 30s after reload — cleared', {
                contactId: state.contactId,
                callHeaderId: state.callHeaderId,
            });
            resetNetworkDropDomain();
        }
    });
}

async function performNetworkDropEnd(state: NetworkDropState) {
    if (networkDropEnding) return;
    networkDropEnding = true;
    clearNetworkDropTimer();

    const live = findLiveContact();
    if (live) {
        const id = live.getContactId();
        if (snapshot.contactId !== id) {
            update({ contactId: id, contact: buildContactInfo(live) });
        }
    }

    const code = parseCallHeaderCode(
        state.callHeaderId ?? snapshot.activeCallData?.callHeaderId,
    );

    try {
        if (code != null) {
            await postSetCallEndedByInterpreter(code);
            callLogger.info('Network-drop set CallEndedBy=Interpreter', {
                code,
                callHeaderId: state.callHeaderId,
                fromReload: state.fromReload,
            });
        }
    } catch (e) {
        callLogger.error('Network-drop setCallEndedByInterpreter failed', {
            callHeaderId: state.callHeaderId,
            error: String(e),
            fromReload: state.fromReload,
        });
        Sentry.captureException(e instanceof Error ? e : new Error(String(e)), {
            tags: { module: 'ccp', ccp_event: 'network_drop_api' },
        });
    }

    // Customer may have hung up while the API ran — no false popup.
    const contactToEnd = findLiveContact() ?? getContactById(snapshot.contactId);
    if (!contactToEnd) {
        callLogger.info('Network-drop end skipped — no live contact (customer hung up?)', {
            callHeaderId: state.callHeaderId,
            fromReload: state.fromReload,
        });
        resetNetworkDropDomain();
        networkDropEnding = false;
        return;
    }

    if (snapshot.contactId !== contactToEnd.getContactId()) {
        update({
            contactId: contactToEnd.getContactId(),
            contact: buildContactInfo(contactToEnd),
        });
    }

    try {
        const agentConn = contactToEnd.getAgentConnection();
        agentConn?.destroy({
            success: () => callLogger.info('Network-drop call ended successfully'),
            failure: (err: any) => {
                callLogger.error('Network-drop Failed to end call', { error: String(err) });
                Sentry.captureException(
                    err instanceof Error ? err : new Error(String(err)),
                    { tags: { module: 'ccp', ccp_event: 'network_drop_end' } },
                );
            },
        });
    } catch (e) {
        callLogger.error('Network-drop endCurrent threw', { error: String(e) });
        Sentry.captureException(e instanceof Error ? e : new Error(String(e)), {
            tags: { module: 'ccp', ccp_event: 'network_drop_end' },
        });
    }

    // SDK failure still shows popup + clears flag to avoid loops.
    resetNetworkDropDomain();
    update({ networkDropNotice: true });
    networkDropEnding = false;
}

/**
 * Single reconcile for CCP network-drop domain transitions.
 * Disconnect arm: Streams disconnected|failed.
 * Reconnect disarm / post-timeout end: navigator.onLine (browserOnline).
 */
function reconcileNetworkDrop(reason: string) {
    const state = readNetworkDropState();
    const connected = snapshot.callPhase === 'connected';
    const sdkDown = isSdkNetworkDown();
    const recovered = isBrowserOnline();
    const live = hasLiveContact();

    // timedOut → end (or clear)
    if (state.phase === 'timedOut') {
        if (state.fromReload) {
            // Reload path: do NOT wait for network healthy — end as soon as a live contact exists.
            if (agentRef && live) {
                callLogger.info('CCP network drop reload end', { reason, contactId: state.contactId });
                void performNetworkDropEnd(state);
                return;
            }
            if (!networkDropTimer) armReloadClearTimer();
            return;
        }

        // Normal path: browser online + live contact → end; online + no contact → clear.
        if (recovered) {
            if (live) {
                callLogger.info('CCP network drop recover end (navigator online)', {
                    reason,
                    contactId: state.contactId,
                });
                void performNetworkDropEnd(state);
            } else {
                callLogger.info('Network-drop timedOut cleared — online with no live contact', {
                    reason,
                });
                resetNetworkDropDomain();
            }
        }
        return;
    }

    // Armed + browser back online → disarm (<25s), even if Streams still says disconnected.
    if (state.phase === 'armed' && recovered) {
        callLogger.info('CCP network drop disarmed — navigator online', { reason });
        resetNetworkDropDomain();
        return;
    }

    // Not on a connected call — never arm.
    if (!connected) {
        if (state.phase === 'armed') {
            resetNetworkDropDomain();
        }
        return;
    }

    // callPhase === connected — arm only on SDK disconnect while browser is offline.
    if (sdkDown) {
        if (recovered) {
            // Streams lag after wifi is back — do not (re)arm.
            return;
        }
        if (state.phase === 'idle') {
            const meta = currentDropCallMeta();
            const next: NetworkDropState = {
                phase: 'armed',
                startedAt: Date.now(),
                fromReload: false,
                callHeaderId: meta.callHeaderId,
                contactId: meta.contactId,
            };
            writeNetworkDropState(next);
            armUnhealthyDropTimer(next.startedAt!);
            callLogger.info('CCP network drop armed', { ...meta, reason });
            return;
        }
        if (state.phase === 'armed' && !networkDropTimer) {
            const next: NetworkDropState = {
                ...state,
                startedAt: Date.now(),
                ...currentDropCallMeta(),
            };
            writeNetworkDropState(next);
            armUnhealthyDropTimer(next.startedAt!);
        }
    }
}

function handleNetworkDropPageHide(event: PageTransitionEvent) {
    if (networkDropUnloadHandled) return;
    if (snapshot.callPhase !== 'connected') return;
    // Frozen into bfcache (tab switch) is not a reload — do not timedOut.
    if (event.persisted) return;
    networkDropUnloadHandled = true;

    const meta = currentDropCallMeta();
    writeNetworkDropState({
        phase: 'timedOut',
        startedAt: Date.now(),
        fromReload: true,
        callHeaderId: meta.callHeaderId,
        contactId: meta.contactId,
    });
    clearNetworkDropTimer();
    callLogger.info('CCP network drop timedOut from pagehide (reload path)', meta);
}

function handleBrowserOnline() {
    console.log('☢️ navigator online', {
        callPhase: snapshot.callPhase,
        sdkStatus: snapshot.networkConnection.status,
    });
    if (!snapshot.browserOnline) update({ browserOnline: true });
    reconcileNetworkDrop('navigator:online');
}

function handleBrowserOffline() {
    console.log('☢️ navigator offline', {
        callPhase: snapshot.callPhase,
        sdkStatus: snapshot.networkConnection.status,
    });
    if (snapshot.browserOnline) update({ browserOnline: false });
    // Arming stays SDK-driven; offline only updates the reconnect signal.
    reconcileNetworkDrop('navigator:offline');
}

function registerNetworkDropUnloadListeners() {
    if (typeof window === 'undefined') return;
    window.addEventListener('pagehide', handleNetworkDropPageHide);
}

function registerBrowserOnlineListeners() {
    if (typeof window === 'undefined') return;
    window.addEventListener('online', handleBrowserOnline);
    window.addEventListener('offline', handleBrowserOffline);
}

registerNetworkDropUnloadListeners();
registerBrowserOnlineListeners();

async function syncNetworkConnectionFromAgent(agent: connect.Agent) {
    const getStatus = (agent as any).getNetworkConnectionStatus;
    if (typeof getStatus !== 'function') return;
    try {
        const result = await getStatus.call(agent);
        if (result?.status) {
            applyNetworkConnectionStatus(
                result.status,
                result.timestamp ?? Date.now(),
                'getNetworkConnectionStatus',
            );
        }
    } catch (e) {
        ccpLogger.warn('getNetworkConnectionStatus failed', { error: String(e) });
    }
}

function applyNetworkConnectionStatus(
    status: string,
    timestamp?: number,
    source: string = 'getNetworkConnectionStatus',
) {
    const allowed: NetworkConnectionStatus[] = [
        'connected',
        'connecting',
        'disconnected',
        'failed',
        'unknown',
    ];
    const normalized = (allowed.includes(status as NetworkConnectionStatus)
        ? status
        : 'unknown') as NetworkConnectionStatus;
    // Preserve a live websocket flag unless Streams reports a healthy connection
    // (which implies the socket is up) or a terminal disconnect/failure.
    let websocketConnected = snapshot.networkConnection.websocketConnected;
    if (normalized === 'connected') {
        websocketConnected = true;
    } else if (normalized === 'disconnected' || normalized === 'failed') {
        websocketConnected = false;
    }

    const prev = snapshot.networkConnection;
    const unchanged =
        prev.status === normalized && prev.websocketConnected === websocketConnected;

    if (!unchanged) {
        console.log('☢️ applyNetworkConnectionStatus', {
            source,
            rawStatus: status,
            normalized,
            websocketConnected,
            previousStatus: prev.status,
            previousWebsocketConnected: prev.websocketConnected,
            callPhase: snapshot.callPhase,
            timestamp: timestamp ?? Date.now(),
        });
    }

    if (unchanged) {
        reconcileNetworkConnectionPolling();
        reconcileNetworkDrop('network-status-unchanged');
        return;
    }

    const sentryLevel: 'info' | 'warning' | 'error' =
        normalized === 'failed'
            ? 'error'
            : normalized === 'disconnected' || normalized === 'unknown'
              ? 'warning'
              : 'info';

    reportCcpNetworkEventToSentry('NetworkConnectionStatus', sentryLevel, {
        status: normalized,
        websocketConnected,
        timestamp,
        previousStatus: prev.status,
        source,
    });

    update({
        networkConnection: {
            status: normalized,
            websocketConnected,
            updatedAt: timestamp ?? Date.now(),
        },
    });
    reconcileNetworkConnectionPolling();
    reconcileNetworkDrop(`network-status:${source}`);
}

function contactHasNativeAutoAccept(c: connect.Contact): boolean {
    return safe(
        () => typeof (c as any).isAutoAcceptEnabled === 'function' && !!(c as any).isAutoAcceptEnabled(),
        false,
    );
}

function getContactAttributeValue(c: connect.Contact, keys: string[]): string | null {
    return safe(() => {
        const raw = c.getAttributes() as Record<string, unknown>;
        for (const key of keys) {
            const entry = raw?.[key];
            if (entry && typeof entry === 'object' && 'value' in entry) return String((entry as { value: unknown }).value);
            if (typeof entry === 'string') return entry;
        }
        return null;
    }, null);
}

function isOnDemandCallType(value: string | null | undefined): boolean {
    if (!value) return false;
    const normalized = value.toLowerCase().replace(/[\s_-]/g, '');
    return normalized === 'ondemandcall' || normalized === 'ondemand';
}

function isScheduledCallType(value: string | null | undefined): boolean {
    if (!value) return false;
    const normalized = value.toLowerCase().replace(/[\s_-]/g, '');
    return (
        normalized === 'schedule' ||
        normalized === 'scheduled' ||
        normalized === 'scheduledcall' ||
        normalized === 'schedulecall' ||
        // Backend vocabulary for the appointment/meeting-PIN path (KinesisPresenceConsumer
        // broadcasts type "meeting_pin_call" for it).
        normalized === 'meetingpincall'
    );
}

function activeCallDataForContact(contactId: string): ActiveCallData | null {
    const fromMap = incomingCallMetaByContactId.get(contactId);
    if (fromMap) return fromMap;

    const data = snapshot.activeCallData;
    if (!data) return null;
    if (data.callId === contactId || data.contactId === contactId) return data;
    return null;
}

/** Call type declared by the contact flow, read straight off the contact. */
function declaredCallType(c: connect.Contact): 'onDemand' | 'scheduled' | null {
    const attrs = buildContactInfo(c).attributes ?? {};
    for (const key of CALL_TYPE_ATTRIBUTE_KEYS) {
        const value = attrs[key];
        if (isOnDemandCallType(value)) return 'onDemand';
        if (isScheduledCallType(value)) return 'scheduled';
    }
    return null;
}

/**
 * NOTE: meetingPin / meetingPinCode are deliberately NOT scheduled markers. On-demand
 * callers verify their identity by PIN too (Verify Caller Flow stamps meetingPinCode from
 * the Lex pin), so those attributes ride on on-demand contacts and used to short-circuit
 * every on-demand auto-accept. Use appointmentId or an explicit scheduled callType.
 */
function contactIsScheduled(c: connect.Contact): boolean {
    const attrs = buildContactInfo(c).attributes ?? {};
    if (attrs.appointmentId || attrs.AppointmentId) return true;
    return declaredCallType(c) === 'scheduled';
}

/**
 * Decided solely by the callType attribute the contact flow stamps on the contact.
 *
 * SignalR/FCM metadata is deliberately NOT consulted: it arrives out-of-band on a separate
 * transport, is keyed by an id that need not equal the Connect contactId, and in production
 * was observed missing, empty (`type: ''`), or wiped by a parallel call's teardown. The
 * contact attribute rides in-band, is keyed correctly by construction, and is re-read on
 * every ring — so it behaves identically on a first offer and on a re-route.
 */
function contactIsOnDemand(c: connect.Contact): boolean {
    if (contactIsScheduled(c)) return false;
    return declaredCallType(c) === 'onDemand';
}

/**
 * Whether this contact plausibly belongs to the armed call. The callHeader comparison is a
 * narrowing filter only: when either side is unavailable we allow, because refusing a
 * contact Amazon Connect explicitly routed to this agent is always worse than accepting a
 * stray one (the backend only routes to an interpreter who already pressed Accept).
 */
function contactMatchesArmedCall(c: connect.Contact): boolean {
    if (!autoAcceptCallHeaderId) return true;
    const code = getContactAttributeValue(c, ['CallHeaderCode', 'callHeaderCode', 'callHeaderId']);
    if (!code) return true;
    return String(code) === String(autoAcceptCallHeaderId);
}

function tryAutoAcceptContact(contactId: string): boolean {
    const c = getContactById(contactId);
    if (!c) return false;
    if (isAcceptingCall) return false;

    const state = safe(() => c.getState()?.type, null);
    if (
        state !== ContactStateType.CONNECTING &&
        state !== ContactStateType.INCOMING
    ) {
        return false;
    }

    // Outbound agent.connect contacts must never call accept() — Connect rejects
    // acceptContact for outbound and it tears the probe down into ERROR/missed.
    if (snapshot.softphoneProbeInFlight || safe(() => c.isInbound?.(), true) === false) {
        callLogger.info('auto-accept skipped — outbound/probe contact (no accept)', {
            contactId,
            softphoneProbeInFlight: snapshot.softphoneProbeInFlight,
            isInbound: safe(() => c.isInbound?.(), null),
        });
        return false;
    }

    const onDemandAutoAccept = contactIsOnDemand(c);
    // Read, never consumed. Amazon Connect re-offers the same call to the same agent after a
    // softphone failure or agent-connect timeout, and every one of those re-rings must
    // auto-accept again. Cleared only on connect, explicit reject, logout, or TTL expiry.
    const armedAutoAccept = snapshot.autoAcceptArmed && contactMatchesArmedCall(c);
    const nativeAutoAccept = contactHasNativeAutoAccept(c);

    if (!onDemandAutoAccept && !armedAutoAccept && !nativeAutoAccept) {
        callLogger.info('auto-accept skipped', {
            contactId,
            armed: snapshot.autoAcceptArmed,
            armedCallHeaderId: autoAcceptCallHeaderId,
            declaredCallType: declaredCallType(c),
            contactAttributes: buildContactInfo(c).attributes ?? {},
        });
        return false;
    }

    if (onDemandAutoAccept) {
        callLogger.info('on-demand call — auto-accepting', { contactId });
    } else if (armedAutoAccept) {
        callLogger.info('interpreter accepted in modal — auto-accepting', {
            contactId,
            armedCallHeaderId: autoAcceptCallHeaderId,
        });
    } else if (nativeAutoAccept) {
        callLogger.info('contact.isAutoAcceptEnabled()=true — auto-accepting', { contactId });
    }

    isAcceptingCall = true;
    update({ callPhase: 'connecting' });
    emit({ type: 'contact:connecting', contactId });
    try {
        c.accept({
            success: () => {
                callLogger.info('auto-accept success', {
                    contactId,
                    onDemandAutoAccept,
                    nativeAutoAccept,
                });
            },
            failure: (err: any) => {
                const errInfo = err && typeof err === 'object'
                    ? { type: (err as any).type, message: (err as any).message, stack: (err as any).stack }
                    : { error: String(err) };
                callLogger.error('streams.contact.accept_failure (auto)', { contactId, ...errInfo });
                isAcceptingCall = false;
                reportSoftphoneFailure('contact.accept_failure', err);
            },
        });
    } catch (e) {
        callLogger.error('auto-accept threw', { contactId, error: String(e) });
        isAcceptingCall = false;
        reportSoftphoneFailure('contact.accept_failure', e);
    }
    return true;
}

function setAgentOfflineSafe(agent: connect.Agent): Promise<void> {
    return new Promise((resolve) => {
        try {
            const current = agent.getState();
            if (current?.type === AgentStateType.OFFLINE) {
                resolve();
                return;
            }
            const offlineState = agent.getAgentStates().find((s: any) => s.type === AgentStateType.OFFLINE);
            if (!offlineState) {
                resolve();
                return;
            }
            agent.setState(offlineState, {
                success: () => resolve(),
                failure: (err: any) => {
                    ccpLogger.error('Failed to set offline for logout', { error: String(err) });
                    resolve();
                },
            }, { enqueueNextState: true });
        } catch (e) {
            ccpLogger.error('setAgentOfflineSafe threw', { error: String(e) });
            resolve();
        }
    });
}

function hitLogoutEndpoint(): Promise<void> {
    return new Promise((resolve) => {
        try {
            const urlMatch = AWS_CONNECT_CCP_URL.match(/https:\/\/([^/]+)/);
            const instanceDomain = urlMatch ? urlMatch[1] : '';
            if (!instanceDomain) {
                resolve();
                return;
            }

            const logoutUrl = `https://${instanceDomain}/logout`;
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.src = logoutUrl;
            iframe.setAttribute('allow', 'microphone *; autoplay *; speaker-selection *');

            let done = false;
            const cleanup = () => {
                if (done) return;
                done = true;
                try { document.body.removeChild(iframe); } catch { /* ignore */ }
                resolve();
            };

            iframe.onload = () => setTimeout(cleanup, 500);
            iframe.onerror = cleanup;
            document.body.appendChild(iframe);
            setTimeout(cleanup, 3000);
        } catch (err) {
            ccpLogger.error('hitLogoutEndpoint threw', { error: String(err) });
            resolve();
        }
    });
}

function stopCallDurationTimer() {
    if (callDurationTimer) {
        clearInterval(callDurationTimer);
        callDurationTimer = null;
    }
}

function startCallDurationTimer(contactId: string) {
    stopCallDurationTimer();
    callDurationTimer = setInterval(() => {
        const c = getContactById(contactId);
        if (!c) return;
        try {
            const ms = c.getStateDuration();
            update({ callDuration: Math.floor(ms / 1000) });
        } catch {
            update({ callDuration: snapshot.callDuration + 1 });
        }
    }, 1000);
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const streamsStore = {
    // ---- React integration ----
    subscribe(l: Listener): () => void {
        listeners.add(l);
        return () => {
            listeners.delete(l);
        };
    },

    getSnapshot(): Snapshot {
        return snapshot;
    },

    onEvent(l: EventListener): () => void {
        eventListeners.add(l);
        return () => {
            eventListeners.delete(l);
        };
    },

    // ---- Bootstrap dispatch: CCP lifecycle ----
    onCcpInitialized() {
        update({
            isInitialized: true,
            ccpAuthState: snapshot.isLoggedIn ? 'authorized' : 'ccp_ready',
        });
    },

    onAckDone() {
        if (!snapshot.isInitialized) {
            update({
                isInitialized: true,
                ccpAuthState: snapshot.isLoggedIn ? 'authorized' : 'ccp_ready',
            });
        }
    },

    onAckTimeout() {
        update({
            isInitialized: true,
            isLoggedIn: false,
            ccpAuthState: 'login_required',
        });
    },

    /** Popup closed without a successful login — return to sign-in UI. */
    onLoginPopupClosed() {
        if (!snapshot.isLoggedIn && snapshot.ccpAuthState === 'authorizing') {
            update({ ccpAuthState: 'login_required' });
        }
    },

    onAuthorizeSuccess() {
        update({ ccpAuthState: 'authorized' });
        emit({ type: 'auth:authorize_success' });
    },

    onAuthorizing() {
        if (snapshot.ccpAuthState !== 'authorized') {
            update({ ccpAuthState: 'authorizing' });
        }
    },

    onAuthFail() {
        update({
            isInitialized: true,
            isLoggedIn: false,
            ccpAuthState: 'session_expired',
        });
    },

    onAccessDenied() {
        update({
            isInitialized: true,
            isLoggedIn: false,
            ccpAuthState: 'access_denied',
        });
    },

    onAuthorizeRetriesExhausted() {
        update({
            isInitialized: true,
            isLoggedIn: false,
            ccpAuthState: 'auth_retries_exhausted',
        });
    },

    onCTIAuthorizeRetriesExhausted() {
        update({
            isInitialized: true,
            isLoggedIn: false,
            ccpAuthState: 'cti_auth_retries_exhausted',
        });
    },

    onIframeRetriesExhausted() {
        update({
            isInitialized: true,
            isLoggedIn: false,
            ccpAuthState: 'auth_retries_exhausted',
        });
    },

    onTerminated() {
        update({ isLoggedIn: false, ccpAuthState: 'login_required' });
        emit({ type: 'logout' });
    },

    // ---- Bootstrap dispatch: agent ----
    onAgentInit(agent: connect.Agent) {
        agentRef = agent;
        const config = agent.getConfiguration();
        const state = agent.getState();

        let dialableCountries: string[] = [];
        try {
            const raw = (agent as any).getDialableCountries?.() || (config as any)?.dialableCountries || [];
            dialableCountries = raw
                .map((c: any) => (typeof c === 'string' ? c.toLowerCase() : (c?.countryCode || '').toLowerCase()))
                .filter(Boolean);
        } catch (e) {
            ccpLogger.warn('Could not read dialable countries', { error: String(e) });
        }

        const voiceEnhancementMode = normalizeVoiceEnhancementMode(
            safe(() => (agent as any).getVoiceEnhancementMode?.(), 'NONE'),
        );

        setLogContext({ agentName: agent.getName(), agentEmail: config.username || '' });
        ccpLogger.info('Agent initialized', {
            agentName: agent.getName(),
            agentEmail: config.username,
            voiceEnhancementMode,
        });

        update({
            isInitialized: true,
            isLoggedIn: true,
            ccpAuthState: 'authorized',
            agentName: agent.getName(),
            agentEmail: config.username || '',
            agentStatus: state?.name || 'Offline',
            dialableCountries,
            voiceEnhancementMode,
        });
        emit({ type: 'agent:initialized' });
        void streamsStore.refreshAudioDevices();
        streamsStore.startMicPermissionMonitoring();
        void syncNetworkConnectionFromAgent(agent);
        try {
            attachCcpWebSocketTelemetry(() =>
                (connectClient.core as {getWebSocketManager?: () => {
                    onConnectionLost?: (cb: () => void) => void
                    onConnectionGain?: (cb: () => void) => void
                } | null}).getWebSocketManager?.() ?? null,
            );
        } catch {
            /* telemetry only */
        }

        // Reload path: agent up — one getContacts via reconcile; arm 30s clear if still waiting.
        const drop = readNetworkDropState();
        if (drop.phase === 'timedOut' && drop.fromReload) {
            armReloadClearTimer();
        }
        reconcileNetworkDrop('agent:initialized');
    },

    onVoiceEnhancementModeChanged(mode: VoiceEnhancementMode | string) {
        const voiceEnhancementMode = normalizeVoiceEnhancementMode(mode);
        ccpLogger.info('Voice enhancement mode changed', { voiceEnhancementMode });
        update({ voiceEnhancementMode });
    },

    onMicrophoneDeviceChanged(deviceId?: string | null) {
        if (!deviceId) return;
        update({ microphoneDeviceId: deviceId });
    },

    onSpeakerDeviceChanged(deviceId?: string | null) {
        if (!deviceId) return;
        update({ speakerDeviceId: deviceId });
    },

    onAgentRefresh(agent: connect.Agent) {
        try {
            noteCcpAgentRefresh();
        } catch {
            /* telemetry only */
        }
        const s = agent.getState();
        const patch: Partial<Snapshot> = {};
        if (s?.name && s.name !== snapshot.agentStatus) {
            patch.agentStatus = s.name;
        }
        const voiceEnhancementMode = normalizeVoiceEnhancementMode(
            safe(() => (agent as any).getVoiceEnhancementMode?.(), snapshot.voiceEnhancementMode),
        );
        if (voiceEnhancementMode !== snapshot.voiceEnhancementMode) {
            patch.voiceEnhancementMode = voiceEnhancementMode;
        }
        if (Object.keys(patch).length > 0) update(patch);
    },

    onAgentStateChange(sc: AgentStateChange) {
        if (sc.newState && sc.newState !== snapshot.agentStatus) {
            update({ agentStatus: sc.newState });
        }
    },

    onMuteToggle(e: MuteToggleEvent) {
        update({ isAgentMuted: e.muted });
        try {
            reportCcpEvent(e?.muted ? 'mute' : 'unmute', { contactId: snapshot.contactId });
        } catch {
            /* telemetry only */
        }
    },

    onAgentOffline() {
        ccpLogger.info('Agent offline event');
    },

    onWebSocketInitFailure() {
        console.log('☢️ onWebSocketInitFailure', {
            callPhase: snapshot.callPhase,
            previousStatus: snapshot.networkConnection.status,
        });
        ccpLogger.warn('WebSocket init failure');
        applyNetworkConnectionStatus('failed', Date.now(), 'onWebSocketInitFailure');
    },

    onNetworkConnectionStatusChanged(event: { status: string; timestamp?: number }) {
        console.log('☢️ onNetworkConnectionStatusChanged', {
            status: event?.status,
            timestamp: event?.timestamp,
            callPhase: snapshot.callPhase,
            previousStatus: snapshot.networkConnection.status,
            browserOnline: snapshot.browserOnline,
        });
        applyNetworkConnectionStatus(
            event?.status || 'unknown',
            event?.timestamp,
            'onNetworkConnectionStatusChanged',
        );
    },

    onSoftphoneError(err: any) {
        ccpLogger.error('Softphone error', serializeStreamsError(err));
        reportSoftphoneFailure('agent.onSoftphoneError', err);
        try {
            reportCcpEvent('softphone_error', {
                contactId: snapshot.contactId,
                detail: extractSoftphoneError(err).errorType,
            });
        } catch {
            /* telemetry only */
        }
    },

    onAgentError(agent?: connect.Agent) {
        const status = safe(() => (agent || agentRef)?.getState()?.name, snapshot.agentStatus);
        ccpLogger.error('Agent error state', { agentStatus: status, callPhase: snapshot.callPhase });
    },

    // ---- Bootstrap dispatch: contact lifecycle ----
    onContactDiscovered(contact: connect.Contact) {
        const id = contact.getContactId();
        const cType = contact.getType();
        const cState = contact.getState().type;
        setLogContext({ contactId: id });
        callLogger.info('Contact discovered', { contactId: id, type: cType, state: cState });

        update({
            contactId: id,
            contact: buildContactInfo(contact),
        });
        void hydrateContactCapabilities(id);
        reconcileNetworkDrop('contact:discovered');
    },

    onConnecting(contactId: string) {
        callLogger.info('onConnecting', { contactId });
        const c = getContactById(contactId);
        if (!c) {
            update({ callPhase: 'ringing', lastMissedCall: null });
            emit({ type: 'contact:connecting', contactId });
            return;
        }

        void hydrateContactCapabilities(contactId);
        hydrateContactSessionState(contactId);

        // Outbound softphone probe: stay on connecting UI, never Accept/Decline, never accept().
        // Softphone CONNECTED is the success signal.
        const isOutbound = safe(() => c.isInbound?.(), true) === false;
        if (snapshot.softphoneProbeInFlight || isOutbound) {
            callLogger.info('outbound/probe connecting — waiting for softphone CONNECTED', {
                contactId,
                softphoneProbeInFlight: snapshot.softphoneProbeInFlight,
                isOutbound,
            });
            update({ callPhase: 'connecting', lastMissedCall: null });
            emit({ type: 'contact:connecting', contactId });
            return;
        }

        if (tryAutoAcceptContact(contactId)) {
            return;
        }

        update({ callPhase: 'ringing', lastMissedCall: null });
        emit({ type: 'contact:connecting', contactId });
    },

    onIncomingNonVoice(contactId: string) {
        callLogger.info('onIncoming', { contactId });
        if (tryAutoAcceptContact(contactId)) {
            return;
        }
        update({ callPhase: 'ringing' });
    },

    onAccepted(contactId: string) {
        callLogger.info('onAccepted', { contactId });
        if (snapshot.callPhase === 'ringing' || snapshot.callPhase === 'idle') {
            update({ callPhase: 'connecting' });
        }
    },

    onConnected(contactId: string) {
        callLogger.info('onConnected', { contactId });
        isAcceptingCall = false;
        // The armed call landed — stop auto-accepting anything that follows.
        streamsStore.cancelAutoAccept();

        startCallDurationTimer(contactId);

        update({ callPhase: 'connected' });

        const c = getContactById(contactId);
        if (c) {
            const caps = snapshotVideoCapabilities(c);
            update({ videoCapabilities: caps });
            if (caps.hasVideoRTC) {
                const agentConn = (c as any).getAgentConnection?.();
                if (agentConn && typeof agentConn.getVideoConnectionInfo === 'function') {
                    agentConn.getVideoConnectionInfo()
                        .then((info: ChimeMeetingInfo) => update({ videoConnectionInfo: info }))
                        .catch(() => {
                            setTimeout(() => {
                                agentConn.getVideoConnectionInfo()
                                    .then((info: ChimeMeetingInfo) => update({ videoConnectionInfo: info }))
                                    .catch(() => callLogger.warn('Could not get video connection info', { contactId }));
                            }, 2000);
                        });
                }
            }
            streamsStore.refreshParticipants();
            ensureVoiceConnectionHoldSubscriptions(c);
            void hydrateContactCapabilities(contactId);
            hydrateContactSessionState(contactId);
        }

        emit({ type: 'contact:connected', contactId });
        try {
            reportContactConnected(contactId);
        } catch {
            /* telemetry only */
        }
        if (snapshot.softphoneFailure) update({ softphoneFailure: null });
        reconcileNetworkDrop('contact:connected');

        if (snapshot.softphoneProbeInFlight) {
            callLogger.info('softphone probe connected (outbound)', { contactId });
            emit({ type: 'softphoneProbe:connected', contactId });
        }
    },

    onContactRefresh(contactId: string, contact: connect.Contact) {
        const info = buildContactInfo(contact);
        if (snapshot.contact?.contactId === contactId && snapshot.contact.contactArn) {
            info.contactArn = snapshot.contact.contactArn;
        }
        update({ contact: info });
        streamsStore.refreshParticipants();
        ensureVoiceConnectionHoldSubscriptions(contact);
        hydrateContactSessionState(contactId);
        try {
            const state = safe(
                () => (contact.getAgentConnection() as any)?.getState?.()?.type ?? null,
                null,
            );
            observeAgentConnectionState(contactId, state);
        } catch {
            /* telemetry only */
        }
    },

    onMissed(contactId: string, contact: connect.Contact) {
        const info = buildContactInfo(contact);
        const queueName = safe(() => contact.getQueue()?.name, undefined);
        const agent = agentRef;
        const diagnostics: Record<string, any> = {
            contactId,
            stateDurationMs: safe(() => contact.getStateDuration(), 0),
            queueName: queueName ?? null,
            agentState: safe(() => agent?.getState(), null),
            softphoneEnabled: safe(() => (agent?.getConfiguration?.() as any)?.softphoneEnabled, null),
            // Prefer contact-level auto-accept (softphoneAutoAccept on AgentConfiguration is deprecated in 2.27).
            isAutoAcceptEnabled: safe(
                () => typeof (contact as any).isAutoAcceptEnabled === 'function' && (contact as any).isAutoAcceptEnabled(),
                null,
            ),
            connections: safe(
                () => contact.getConnections().map((c: any) => ({ type: c.getType(), state: c.getState().type })),
                [],
            ),
            autoAcceptArmed: snapshot.autoAcceptArmed,
            callPhase: snapshot.callPhase,
        };
        callLogger.error('streams.contact.missed', diagnostics);

        isAcceptingCall = false;
        // Auto-accept stays armed: Connect routinely re-offers a missed contact to the same
        // agent, and the interpreter already pressed Accept for this call.
        stopCallDurationTimer();

        update({
            callPhase: 'idle',
            callDuration: 0,
            isCallOnHold: false,
            conferenceParticipants: [],
            videoCapabilities: null,
            videoConnectionInfo: null,
            hasTwoActiveParticipants: false,
            isUnderSupervision: false,
            softphoneFailure: null,
            lastMissedCall: {
                contactId,
                phoneNumber: info.phoneNumber,
                callerDisplayName: info.callerDisplayName,
                queueName,
                at: Date.now(),
            },
        });
        emit({ type: 'contact:missed', contactId, diagnostics });
    },

    dismissMissedCall() {
        if (!snapshot.lastMissedCall) return;
        update({ lastMissedCall: null });
    },

    onACW(contactId: string) {
        callLogger.info('onACW', { contactId });
        stopCallDurationTimer();
        resetNetworkDropDomain();
        update({
            callPhase: 'acw',
            callDuration: 0,
            isCallOnHold: false,
            agentStatus: 'AfterCallWork',
        });
    },

    onEnded(contactId: string) {
        callLogger.info('onEnded', { contactId });
        stopCallDurationTimer();
        finishAddParticipant('contact ended');
        resetNetworkDropDomain();
        update({
            callPhase: 'acw',
            callDuration: 0,
            isCallOnHold: false,
            agentStatus: 'AfterCallWork',
        });
        emit({ type: 'contact:ended', contactId });
        try {
            reportContactEnded(contactId);
        } catch {
            /* telemetry only */
        }
    },

    onDestroy(contactId: string) {
        callLogger.info('onDestroy', { contactId });
        const hadSoftphoneFailure =
            !!snapshot.softphoneFailure || snapshot.callPhase === 'failed';
        clearCallContext();
        isAcceptingCall = false;
        stopCallDurationTimer();
        disconnectedParticipantIds = new Set();
        shouldAutoConference = false;
        clearVoiceConnectionSubscriptions();
        finishAddParticipant('contact destroyed');

        incomingCallMetaByContactId.delete(contactId);
        // Manual/customer teardown — clear drop domain; keep networkDropNotice if already set.
        resetNetworkDropDomain();
        update({
            callPhase: 'idle',
            contactId: null,
            contact: null,
            callDuration: 0,
            isCallOnHold: false,
            videoCapabilities: null,
            videoConnectionInfo: null,
            conferenceParticipants: [],
            activeCallData: null,
            canTransferContact: null,
            hasTwoActiveParticipants: false,
            isUnderSupervision: false,
            softphoneFailure: null,
            softphoneProbeInFlight: false,
        });
        emit({ type: 'contact:destroyed', contactId });
        try {
            clearAgentConnectionState(contactId);
        } catch {
            /* telemetry only */
        }
    },

    /**
     * ContactEvents.ERROR fires on the CONNECTING/INCOMING → ERROR transition and hands us
     * `new connect.Contact(contactId)` — an object whose only property is contactId. It has
     * no errorType and no message, so it cannot classify a softphone failure; routing it into
     * reportSoftphoneFailure made every missed call fabricate a bogus "Connection failed" and
     * made the real softphone-error rate unmeasurable. The same transition also raises
     * ContactEvents.MISSED, which onMissed already handles. Genuine softphone failures arrive
     * on AgentEvents.SOFTPHONE_ERROR (onSoftphoneError) carrying a real SoftphoneError.
     */
    onContactError(contactId: string, err: any) {
        callLogger.warn('contact entered ERROR state', { contactId, ...serializeStreamsError(err) });
    },

    clearSoftphoneFailure() {
        if (!snapshot.softphoneFailure) return;
        update({ softphoneFailure: null });
    },

    dismissConnectionFailure() {
        if (snapshot.callPhase !== 'failed' && !snapshot.softphoneFailure) return;

        isAcceptingCall = false;
        streamsStore.cancelAutoAccept();

        const c = getContactById(snapshot.contactId);
        if (c) {
            const state = safe(() => c.getState()?.type, null);
            if (state === ContactStateType.INCOMING) {
                streamsStore.rejectCurrent();
            } else {
                streamsStore.endCurrent();
            }
        }

        update({ softphoneFailure: null, callPhase: 'idle' });
    },

    syncVoiceEnhancementFromAgent() {
        if (!agentRef) return;
        const voiceEnhancementMode = normalizeVoiceEnhancementMode(
            safe(() => (agentRef as any).getVoiceEnhancementMode?.(), snapshot.voiceEnhancementMode),
        );
        if (voiceEnhancementMode !== snapshot.voiceEnhancementMode) {
            update({ voiceEnhancementMode });
        }
    },

    async refreshMicPermission(): Promise<MicPermissionState> {
        const state = await queryMicPermission();
        if (state !== snapshot.micPermission) update({ micPermission: state });
        return state;
    },

    /** Probe browser mic permission + getUserMedia without surfacing a softphone failure notice. */
    async evaluateMicrophoneAccess(): Promise<boolean> {
        const queried = await queryMicPermission();
        if (queried === 'denied') {
            if (snapshot.micPermission !== 'denied') update({ micPermission: 'denied' });
            return false;
        }

        const ok = await ensureMicAccess();
        const next: MicPermissionState = ok ? 'granted' : 'denied';
        if (snapshot.micPermission !== next) update({ micPermission: next });
        return ok;
    },

    startMicPermissionMonitoring() {
        stopMicPermissionMonitoring();
        void streamsStore.evaluateMicrophoneAccess();

        void (async () => {
            try {
                const perms = navigator.permissions;
                if (!perms?.query) return;
                const status = await perms.query({ name: 'microphone' as PermissionDescriptor['name'] });
                micPermissionStatus = status;
                micPermissionChangeHandler = () => {
                    void streamsStore.evaluateMicrophoneAccess();
                };
                status.addEventListener('change', micPermissionChangeHandler);
            } catch {
                /* Permissions-Policy / browser quirks */
            }
        })();
    },

    async ensureMicrophoneReady(): Promise<boolean> {
        const ok = await streamsStore.evaluateMicrophoneAccess();
        if (!ok) {
            reportSoftphoneFailure(
                'mic_guardrail',
                { errorType: 'microphone_not_shared', message: 'Microphone permission denied' },
            );
        }
        return ok;
    },

    // ---- Auto-accept arming ----
    /**
     * Record that the interpreter pressed Accept in the incoming-call modal so the CCP
     * contact is answered whenever Amazon Connect delivers it — however late, and however
     * many times Connect re-offers it.
     *
     * The arm intentionally survives contact:missed and softphone failures, because those
     * are precisely when Connect re-routes the same call back to this agent. It is cleared
     * only by success (onConnected), an explicit decline, logout, or the TTL.
     */
    armAutoAccept(options: { ttlMs?: number; meta?: ActiveCallData | null; callHeaderId?: string | null } = {}) {
        const ttlMs = options.ttlMs ?? AUTO_ACCEPT_ARM_TTL_MS;
        const { meta, callHeaderId } = options;

        if (meta) streamsStore.syncIncomingCallMeta(meta);

        if (autoAcceptTimer) clearTimeout(autoAcceptTimer);
        autoAcceptCallHeaderId = callHeaderId ? String(callHeaderId) : null;
        update({ autoAcceptArmed: true });
        callLogger.info('auto-accept armed', {
            ttlMs,
            callHeaderId: autoAcceptCallHeaderId,
        });

        autoAcceptTimer = setTimeout(() => {
            autoAcceptTimer = null;
            autoAcceptCallHeaderId = null;
            if (!snapshot.autoAcceptArmed) return;
            update({ autoAcceptArmed: false });
            callLogger.warn('auto-accept window expired', { ttlMs });
            emit({ type: 'autoAccept:expired' });
        }, ttlMs);
    },

    consumeAutoAccept(): boolean {
        return snapshot.autoAcceptArmed;
    },

    cancelAutoAccept() {
        if (autoAcceptTimer) {
            clearTimeout(autoAcceptTimer);
            autoAcceptTimer = null;
        }
        autoAcceptCallHeaderId = null;
        if (snapshot.autoAcceptArmed) {
            callLogger.info('auto-accept disarmed');
            update({ autoAcceptArmed: false });
        }
    },

    // Legacy public-API compatibility: useAWSConnect().setShouldAutoAcceptCall(boolean)
    setShouldAutoAcceptCall(value: boolean) {
        if (value) streamsStore.armAutoAccept();
        else streamsStore.cancelAutoAccept();
    },

    // ---- Active call metadata ----
    syncIncomingCallMeta(data: ActiveCallData) {
        update({ activeCallData: data });
        const contactId = data.contactId || data.callId;
        if (contactId) {
            incomingCallMetaByContactId.set(contactId, data);
        }
    },

    setActiveCallData(data: ActiveCallData | null) {
        if (!data) {
            update({ activeCallData: null });
            return;
        }
        streamsStore.syncIncomingCallMeta(data);
    },

    // ---- Call actions ----
    acceptCurrent() {
        void (async () => {
            const c = getContactById(snapshot.contactId);
            if (!c) {
                callLogger.warn('acceptCurrent: no contact');
                return;
            }
            if (isAcceptingCall) return;

            const micOk = await streamsStore.ensureMicrophoneReady();
            if (!micOk) {
                callLogger.warn('acceptCurrent blocked — microphone not available');
                return;
            }

            const state = safe(() => c.getState()?.type, null);
            // Voice "connecting" is the valid accept state; non-voice uses "incoming".
            if (
                state !== ContactStateType.CONNECTING &&
                state !== ContactStateType.INCOMING
            ) {
                callLogger.warn('acceptCurrent called in unexpected state', { state });
            }

            isAcceptingCall = true;
            update({ callPhase: 'connecting' });
            callLogger.info('Manually accepting call', { contactId: snapshot.contactId });
            try {
                c.accept({
                    success: () => callLogger.info('Manual accept success'),
                    failure: (err: any) => {
                        const info = err && typeof err === 'object'
                            ? { type: (err as any).type, message: (err as any).message }
                            : { error: String(err) };
                        callLogger.error('streams.contact.accept_failure (manual)', { ...info });
                        isAcceptingCall = false;
                        reportSoftphoneFailure('contact.accept_failure', err);
                    },
                });
            } catch (e) {
                callLogger.error('acceptCurrent threw', { error: String(e) });
                isAcceptingCall = false;
                reportSoftphoneFailure('contact.accept_failure', e);
            }
        })();
    },

    rejectCurrent() {
        // An explicit decline overrides any armed accept.
        streamsStore.cancelAutoAccept();
        const c = getContactById(snapshot.contactId);
        if (!c) return;
        const agentConn = c.getAgentConnection();
        if (!agentConn) {
            callLogger.warn('rejectCurrent: no agent connection');
            return;
        }
        agentConn.destroy({
            success: () => callLogger.info('Manual reject success'),
            failure: (err: any) => callLogger.error('Manual reject failed', { error: String(err) }),
        });
    },

    endCurrent() {
        // Manual End Call (and any other caller) clears network-drop so we never double-end.
        resetNetworkDropDomain();
        const c = getContactById(snapshot.contactId);
        if (!c) {
            callLogger.warn('endCurrent: no contact');
            return;
        }
        const agentConn = c.getAgentConnection();
        agentConn?.destroy({
            success: () => callLogger.info('Call ended successfully'),
            failure: (err: any) => callLogger.error('Failed to end call', { error: String(err) }),
        });
    },

    dismissNetworkDropNotice() {
        if (!snapshot.networkDropNotice) return;
        update({ networkDropNotice: false });
    },

    clearCurrent() {
        // ACW teardown uses contact.clear() (not deprecated contact.complete()).
        // Agent state after ACW is owned by the routing profile's "After contact work"
        // next-status setting and reported via onAgentStateChange / onAgentRefresh.
        const c = getContactById(snapshot.contactId);
        if (!c) {
            callLogger.breadcrumb('clearCurrent: no contact - resetting');
            update({
                callPhase: 'idle',
                contactId: null,
                contact: null,
                activeCallData: null,
            });
            return;
        }
        c.clear({
            success: () => callLogger.info('Contact cleared'),
            failure: (err: any) => {
                if (err?.type === 'ContactNotFoundException') {
                    callLogger.breadcrumb('Contact already cleared');
                    update({
                        callPhase: 'idle',
                        contactId: null,
                        contact: null,
                        activeCallData: null,
                    });
                } else {
                    callLogger.error('Failed to clear contact', { error: String(err) });
                }
            },
        });
    },

    async toggleHoldCurrent() {
        const c = getContactById(snapshot.contactId);
        if (!c) {
            callLogger.warn('toggleHoldCurrent: no contact');
            return;
        }
        if (holdAllInFlight) {
            callLogger.breadcrumb('toggleHoldCurrent ignored — already in flight');
            return;
        }

        const allHeld = areAllParticipantsOnHold(c);
        holdAllInFlight = true;

        if (allHeld) {
            // Resume All — optimistic UI, then resume SEQUENTIALLY.
            // Connect rejects concurrent hold/resume ops on the same contact, so
            // parallel resumes only landed on one connection. Run one at a time.
            update({ isCallOnHold: false });
            const holdable = getHoldableConnections(c, 'resume');
            if (holdable.length === 0) {
                holdAllInFlight = false;
                streamsStore.refreshParticipants();
                return;
            }
            for (const conn of holdable) {
                setOptimisticHold(safe(() => conn.getConnectionId(), undefined), false);
            }
            streamsStore.refreshParticipants();
            for (const conn of holdable) {
                await resumeConnection(conn);
            }
            holdAllInFlight = false;
            streamsStore.refreshParticipants();
            syncAllHoldFlag(getContactById(snapshot.contactId));
        } else {
            // Hold All — optimistic UI, then hold SEQUENTIALLY (same concurrency constraint).
            update({ isCallOnHold: true });
            const holdable = getHoldableConnections(c, 'hold');
            if (holdable.length === 0) {
                holdAllInFlight = false;
                syncAllHoldFlag(c);
                return;
            }
            for (const conn of holdable) {
                setOptimisticHold(safe(() => conn.getConnectionId(), undefined), true);
            }
            streamsStore.refreshParticipants();
            for (const conn of holdable) {
                await holdConnection(conn);
            }
            holdAllInFlight = false;
            streamsStore.refreshParticipants();
            syncAllHoldFlag(getContactById(snapshot.contactId));
        }
    },

    // ---- Agent actions ----
    setAgentAvailable(options?: { force?: boolean }) {
        const agent = agentRef;
        if (!agent) {
            ccpLogger.warn('setAgentAvailable: agent not initialized');
            return;
        }

        const activePhases: CallPhase[] = ['ringing', 'connecting', 'connected'];
        if (!options?.force && activePhases.includes(snapshot.callPhase)) {
            ccpLogger.warn('setAgentAvailable blocked - active contact', { phase: snapshot.callPhase });
            return;
        }

        const applyRoutable = () => {
            if (safe(() => agent.getState()?.type, null) === AgentStateType.ROUTABLE) {
                update({ agentStatus: 'Available' });
                return;
            }

            const routable = agent.getAgentStates().find((s: any) => s.type === AgentStateType.ROUTABLE);
            if (!routable) {
                ccpLogger.warn('Routable state not available in agent routing profile');
                return;
            }
            agent.setState(routable, {
                success: () => {
                    ccpLogger.info('Set agent to Available');
                    update({ agentStatus: 'Available' });
                },
                failure: (err: any) => ccpLogger.error('Failed to set Available', { error: String(err) }),
            });
        };

        if (options?.force) {
            applyRoutable();
            return;
        }

        // Pre-shift mic guardrail — avoid going Available without a usable mic.
        void (async () => {
            const micOk = await streamsStore.ensureMicrophoneReady();
            if (!micOk) {
                ccpLogger.warn('setAgentAvailable blocked — microphone not available');
                return;
            }
            applyRoutable();
        })();
    },

    setAgentOffline() {
        const agent = agentRef;
        if (!agent) {
            ccpLogger.warn('setAgentOffline: agent not initialized');
            return;
        }
        if (safe(() => agent.getState()?.type, null) === AgentStateType.OFFLINE) {
            update({ agentStatus: 'Offline' });
            return;
        }
        const offline = agent.getAgentStates().find((s: any) => s.type === AgentStateType.OFFLINE);
        if (!offline) return;
        agent.setState(offline, {
            success: () => {
                ccpLogger.info('Set agent to Offline');
                update({ agentStatus: 'Offline' });
            },
            failure: (err: any) => ccpLogger.error('Failed to set Offline', { error: String(err) }),
        });
    },

    toggleAgentMute() {
        const agent = agentRef;
        if (!agent) {
            ccpLogger.warn('toggleAgentMute: agent not initialized');
            return;
        }
        const next = !snapshot.isAgentMuted;
        update({ isAgentMuted: next });
        try {
            if (next) agent.mute();
            else agent.unmute();
        } catch (e) {
            ccpLogger.error('toggleAgentMute threw', { error: String(e) });
        }
    },

    async logout() {
        const agent = agentRef;
        if (!agent) {
            ccpLogger.info('logout: no agent - finalizing');
            streamsStore._finalizeLogout();
            return;
        }

        try {
            await setAgentOfflineSafe(agent);
            await hitLogoutEndpoint();
            try {
                const core = (connectClient as any).core;
                const upstream = core?.getUpstream?.() || core?.upstream;
                upstream?.sendUpstream((connectClient as any).EventType.TERMINATE);
            } catch (e) {
                ccpLogger.warn('Could not send TERMINATE upstream', { error: String(e) });
            }
        } catch (e) {
            ccpLogger.error('logout sequence error', { error: String(e) });
        }

        streamsStore._finalizeLogout();
    },

    _finalizeLogout() {
        stopMicPermissionMonitoring();
        stopNetworkConnectionPolling();
        agentRef = null;
        stopCallDurationTimer();
        if (autoAcceptTimer) {
            clearTimeout(autoAcceptTimer);
            autoAcceptTimer = null;
        }
        autoAcceptCallHeaderId = null;
        clearCallContext();
        snapshot = { ...initialSnapshot, ccpAuthState: 'ccp_loading' };
        notify();
        emit({ type: 'logout', reinitCcp: true });
    },

    // ---- Conference ----
    refreshParticipants() {
        const c = getContactById(snapshot.contactId);
        if (!c) {
            if (snapshot.conferenceParticipants.length > 0) update({ conferenceParticipants: [] });
            return;
        }

        ensureVoiceConnectionHoldSubscriptions(c);
        const list = snapshotParticipants(c);
        const flags = snapshotContactSessionFlags(c);
        update({
            conferenceParticipants: list,
            hasTwoActiveParticipants: flags.hasTwoActiveParticipants,
            isUnderSupervision: flags.isUnderSupervision,
        });
        syncAllHoldFlag(c);
        clearAddingParticipantIfOutboundSettled(c);

        if (shouldAutoConference) {
            const currentState = safe(() => c.getState?.()?.type, null);
            const ending = currentState === ContactStateType.ENDED || currentState === ContactStateType.MISSED;
            if (ending) {
                callLogger.breadcrumb('Auto-conference skipped — contact ending', { currentState });
                shouldAutoConference = false;
                return;
            }
            const connections = c.getConnections();
            const newP = connections.find((cc: any) => cc !== c.getInitialConnection() && cc.isConnected());
            if (newP) {
                shouldAutoConference = false;
                callLogger.info('Auto-conference triggered');
                c.conferenceConnections({
                    success: () => {
                        callLogger.info('Auto-conference success');
                        streamsStore.refreshParticipants();
                    },
                    failure: (e: any) => {
                        const es = String(e);
                        if (es.includes('CallAlreadyConferencedException')) {
                            streamsStore.refreshParticipants();
                        } else if (es.includes('InvalidStateException')) {
                            shouldAutoConference = true;
                            streamsStore.refreshParticipants();
                        } else {
                            shouldAutoConference = true;
                            callLogger.warn('Auto-conference failed', { error: es });
                        }
                    },
                });
            }
        }
    },

    async addParticipant(phoneNumber: string): Promise<void> {
        if (!phoneNumber) return;
        const c = getContactById(snapshot.contactId);
        if (!c) {
            callLogger.warn('addParticipant: no active contact');
            return;
        }

        if (isAddingParticipantInFlight || snapshot.isAddingParticipant) {
            callLogger.warn('addParticipant: already in progress');
            return Promise.reject(new Error('addParticipant already in progress'));
        }

        // Optional Streams API (removed in 2.27.3+): gate add/transfer when Connect reports it is not allowed.
        if (typeof (c as any).canTransferContact === 'function') {
            try {
                const canTransfer = await (c as any).canTransferContact();
                update({ canTransferContact: !!canTransfer });
                if (!canTransfer) {
                    callLogger.warn('addParticipant blocked — canTransferContact=false');
                    return Promise.reject(new Error('This contact cannot be transferred or have participants added right now'));
                }
            } catch (e) {
                callLogger.warn('canTransferContact check failed — proceeding', { error: String(e) });
            }
        }

        isAddingParticipantInFlight = true;
        setIsAddingParticipant(true, 'addParticipant started');

        return new Promise((resolve, reject) => {
            let settled = false;
            const settle = (outcome: 'resolve' | 'reject', reason: string, err?: unknown) => {
                if (settled) return;
                settled = true;
                finishAddParticipant(reason);
                if (outcome === 'resolve') resolve();
                else reject(err instanceof Error ? err : new Error(String(err)));
            };

            addParticipantTimer = setTimeout(() => {
                addParticipantTimer = null;
                if (!settled) {
                    callLogger.warn('addParticipant timed out', { phoneNumber, timeoutMs: ADD_PARTICIPANT_TIMEOUT_MS });
                    settle('reject', 'addParticipant timed out', new Error('addParticipant timed out'));
                }
            }, ADD_PARTICIPANT_TIMEOUT_MS);

            const endpoint = createPhoneEndpoint(phoneNumber);
            c.addConnection(endpoint, {
                success: () => {
                    callLogger.info('Third party dialing', { phoneNumber });
                    shouldAutoConference = true;
                    streamsStore.refreshParticipants();
                    setTimeout(() => streamsStore.refreshParticipants(), 1000);
                    settle('resolve', 'addConnection success');
                },
                failure: (err: any) => {
                    callLogger.error('Failed to add participant', { error: String(err) });
                    settle('reject', 'addConnection failure', err);
                },
            });
        });
    },

    holdParticipant(connectionId: string) {
        const c = getContactById(snapshot.contactId);
        if (!c) return;
        const agentConnId = safe(() => c.getAgentConnection()?.getConnectionId(), null);
        const conn = c.getConnections().find((x: any) => x.getConnectionId() === connectionId);
        if (!conn || !isHoldableForHoldAction(conn, agentConnId)) {
            callLogger.warn('holdParticipant: connection not holdable', { connectionId });
            return;
        }
        setOptimisticHold(connectionId, true);
        streamsStore.refreshParticipants();
        void holdConnection(conn).then(() => {
            streamsStore.refreshParticipants();
            syncAllHoldFlag(getContactById(snapshot.contactId));
        });
    },

    async resumeParticipant(connectionId: string) {
        const c = getContactById(snapshot.contactId);
        if (!c) return;
        const agentConnId = safe(() => c.getAgentConnection()?.getConnectionId(), null);
        const conn = c.getConnections().find((x: any) => x.getConnectionId() === connectionId);
        if (!conn || !isHoldableForResumeAction(conn, agentConnId)) {
            callLogger.warn('resumeParticipant: connection not resumable', { connectionId });
            return;
        }

        // Non-blocking canResume hint — don't stall the UI on this API.
        if (typeof (conn as any).canResumeParticipant === 'function') {
            void (conn as any)
                .canResumeParticipant()
                .then((canResume: boolean) => {
                    canResumeByConnectionId.set(connectionId, !!canResume);
                    if (!canResume) {
                        callLogger.warn('canResumeParticipant=false (resume already started)', { connectionId });
                    }
                })
                .catch(() => { /* ignore */ });
        }

        setOptimisticHold(connectionId, false);
        streamsStore.refreshParticipants();
        await resumeConnection(conn);
        streamsStore.refreshParticipants();
        syncAllHoldFlag(getContactById(snapshot.contactId));
    },

    disconnectParticipant(connectionId: string) {
        const c = getContactById(snapshot.contactId);
        if (!c) return;
        const conn = c.getConnections().find((x: any) => x.getConnectionId() === connectionId);
        conn?.destroy({
            success: () => {
                callLogger.info('Participant disconnected', { connectionId });
                disconnectedParticipantIds.add(connectionId);
                finishAddParticipant('participant disconnected');
                setTimeout(() => streamsStore.refreshParticipants(), 1000);
            },
            failure: (err: any) => callLogger.error('Failed to disconnect participant', { error: String(err) }),
        });
    },

    conferenceAll() {
        const c = getContactById(snapshot.contactId);
        if (!c) return;
        c.conferenceConnections({
            success: () => {
                callLogger.info('All parties conferenced');
                streamsStore.refreshParticipants();
            },
            failure: (err: any) => callLogger.error('Failed to conference', { error: String(err) }),
        });
    },

    // ---- Dialpad ----
    sendDtmf(digits: string) {
        if (!digits) return;
        try {
            const c = getContactById(snapshot.contactId);
            let connection = c?.getActiveInitialConnection();
            if (!connection && agentRef) {
                const contacts = agentRef.getContacts();
                if (contacts && contacts.length > 0) {
                    connection = contacts[0].getActiveInitialConnection();
                }
            }
            if (!connection) {
                callLogger.warn('No active connection for DTMF');
                return;
            }
            connection.sendDigits(digits, {
                success: () => callLogger.breadcrumb('DTMF sent', { digits }),
                failure: (err: any) => callLogger.error('Failed to send DTMF', { error: String(err) }),
            });
        } catch (e) {
            callLogger.error('sendDtmf threw', { error: String(e) });
        }
    },

    async dialNumber(phoneNumber: string): Promise<void> {
        if (!phoneNumber) return;
        if (isDialing) return;
        if (!agentRef) {
            throw new Error('Agent not initialized');
        }
        isDialing = true;
        return new Promise((resolve, reject) => {
            try {
                const endpoint = createPhoneEndpoint(phoneNumber);
                agentRef!.connect(endpoint, {
                    success: () => {
                        callLogger.info('Dialing', { phoneNumber });
                        isDialing = false;
                        resolve();
                    },
                    failure: (err: any) => {
                        callLogger.error('Failed to dial', { error: String(err) });
                        isDialing = false;
                        reject(err);
                    },
                });
            } catch (e) {
                isDialing = false;
                callLogger.error('dialNumber threw', { error: String(e) });
                reject(e);
            }
        });
    },

    /**
     * Outbound softphone probe: agent.connect to a probe/auto-answer number.
     * Works while Offline. Unlock the recovery lock when softphoneProbe:connected fires.
     */
    async startSoftphoneProbeOutbound(phoneNumber: string): Promise<void> {
        const normalized = String(phoneNumber || '').replace(/\s+/g, '');
        if (!normalized) {
            throw new Error('Softphone probe number is not configured');
        }
        if (snapshot.softphoneProbeInFlight) {
            throw new Error('Softphone probe already in progress');
        }
        update({ softphoneProbeInFlight: true });
        callLogger.info('softphone probe outbound starting', { phoneNumber: normalized });
        try {
            await streamsStore.dialNumber(normalized);
        } catch (e) {
            update({ softphoneProbeInFlight: false });
            throw e;
        }
    },

    clearSoftphoneProbeInFlight() {
        if (!snapshot.softphoneProbeInFlight) return;
        update({ softphoneProbeInFlight: false });
    },

    /**
     * Sets agent voice/audio enhancement mode (Streams ≥ 2.24; custom CCP needs ≥ 2.25.2).
     * Modes: NONE | NOISE_SUPPRESSION | VOICE_ISOLATION (wired headset recommended for isolation).
     */
    async setVoiceEnhancementMode(mode: VoiceEnhancementMode): Promise<void> {
        const next = normalizeVoiceEnhancementMode(mode);
        if (!agentRef || typeof (agentRef as any).setVoiceEnhancementMode !== 'function') {
            ccpLogger.warn('setVoiceEnhancementMode unavailable');
            throw new Error('Voice enhancement is not available');
        }
        try {
            await (agentRef as any).setVoiceEnhancementMode(next);
            update({ voiceEnhancementMode: next });
            ccpLogger.info('Voice enhancement mode set', { voiceEnhancementMode: next });
        } catch (e) {
            ccpLogger.error('setVoiceEnhancementMode failed', { error: String(e), mode: next });
            throw e instanceof Error ? e : new Error(String(e));
        }
    },

    async refreshAudioDevices(): Promise<void> {
        const isOnCall =
            snapshot.callPhase === 'ringing' ||
            snapshot.callPhase === 'connecting' ||
            snapshot.callPhase === 'connected';
        try {
            const devices = await listMediaDevices({ requestPermission: !isOnCall });
            const microphones = devices
                .map(toAudioDeviceOption)
                .filter((d): d is AudioDeviceOption => !!d && d.kind === 'audioinput');
            const speakers = devices
                .map(toAudioDeviceOption)
                .filter((d): d is AudioDeviceOption => !!d && d.kind === 'audiooutput');

            const patch: Partial<Snapshot> = { microphones, speakers };
            if (!snapshot.microphoneDeviceId && microphones[0]) {
                patch.microphoneDeviceId = microphones[0].deviceId;
            }
            if (!snapshot.speakerDeviceId && speakers[0]) {
                patch.speakerDeviceId = speakers[0].deviceId;
            }
            update(patch);
        } catch (e) {
            ccpLogger.warn('refreshAudioDevices failed', { error: String(e) });
        }
    },

    async setMicrophoneDevice(deviceId: string): Promise<void> {
        if (!agentRef || typeof (agentRef as any).setMicrophoneDevice !== 'function') {
            throw new Error('Microphone device selection is not available');
        }
        if (!deviceId) throw new Error('Microphone device id is required');
        try {
            (agentRef as any).setMicrophoneDevice(deviceId);
            update({ microphoneDeviceId: deviceId });
            ccpLogger.info('Microphone device set', { deviceId });
        } catch (e) {
            ccpLogger.error('setMicrophoneDevice failed', { error: String(e), deviceId });
            throw e instanceof Error ? e : new Error(String(e));
        }
    },

    async setSpeakerDevice(deviceId: string): Promise<void> {
        if (!agentRef || typeof (agentRef as any).setSpeakerDevice !== 'function') {
            throw new Error('Speaker device selection is not available');
        }
        if (!deviceId) throw new Error('Speaker device id is required');
        try {
            (agentRef as any).setSpeakerDevice(deviceId);
            update({ speakerDeviceId: deviceId });
            ccpLogger.info('Speaker device set', { deviceId });
        } catch (e) {
            ccpLogger.error('setSpeakerDevice failed', { error: String(e), deviceId });
            throw e instanceof Error ? e : new Error(String(e));
        }
    },

};

// Test-only reset hook — not part of the public API.
export function __resetStreamsStoreForTests() {
    stopMicPermissionMonitoring();
    stopNetworkConnectionPolling();
    resetNetworkDropDomain();
    networkDropUnloadHandled = false;
    networkDropEnding = false;
    snapshot = initialSnapshot;
    listeners.clear();
    eventListeners.clear();
    agentRef = null;
    if (autoAcceptTimer) clearTimeout(autoAcceptTimer);
    autoAcceptTimer = null;
    autoAcceptCallHeaderId = null;
    if (callDurationTimer) clearInterval(callDurationTimer);
    callDurationTimer = null;
    shouldAutoConference = false;
    disconnectedParticipantIds = new Set();
    isAcceptingCall = false;
    isDialing = false;
    isAddingParticipantInFlight = false;
    clearAddParticipantTimer();
    clearVoiceConnectionSubscriptions();
}
